import React, { useEffect, useRef, useState } from "react";
import { api } from "../../../api/api";
import { tr } from "../../../i18n";

/**
 * Settings for the Västtrafik departures widget: stop (searched via our server), number of
 * rows and colours. Calls onPatch with the changed departuresObj fields.
 */
export default function DeparturesPanel({ obj, onPatch }) {
  const o = obj || {};
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [error, setError] = useState("");
  const timer = useRef(null);

  useEffect(() => {
    clearTimeout(timer.current);
    if (query.trim().length < 2) {
      setResults([]);
      return undefined;
    }
    timer.current = setTimeout(async () => {
      try {
        const { data } = await api.get(`/transit/stops?q=${encodeURIComponent(query.trim())}`);
        setResults(data.stops || []);
        setError("");
      } catch (e) {
        setResults([]);
        setError(
          e?.response?.status === 503 ? tr("Västtrafik is not set up on the server yet.") : tr("Could not search stops.")
        );
      }
    }, 300);
    return () => clearTimeout(timer.current);
  }, [query]);

  const color = (label, key, fallback) => (
    <div className="de-rs-row" style={{ alignItems: "center", marginBottom: 8 }}>
      <label className="de-rs-inline-label" style={{ minWidth: 100, fontSize: 12 }}>{label}</label>
      <input
        type="color"
        value={o[key] || fallback}
        onChange={(e) => onPatch({ [key]: e.target.value })}
        style={{ width: 36, height: 28, border: "none", padding: 0, cursor: "pointer", borderRadius: 4 }}
      />
    </div>
  );

  return (
    <div className="de-rs-section">
      <span className="de-rs-section-label">{tr("Departures")}</span>
      <div className="de-rs-field" style={{ marginBottom: 8 }}>
        <label>{tr("Stop")}</label>
        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>{o.gid ? o.stopName : tr("No stop selected")}</div>
        <input
          type="text"
          value={query}
          placeholder={tr("Search stop, e.g. Brunnsparken")}
          onChange={(e) => setQuery(e.target.value)}
          style={{ width: "100%", height: 32, borderRadius: 6, border: "1px solid #d0d5dd", padding: "0 8px" }}
        />
        {error && <div style={{ color: "#b42318", fontSize: 12, marginTop: 6 }}>{error}</div>}
        {results.length > 0 && (
          <div style={{ border: "1px solid #eaecf0", borderRadius: 6, marginTop: 6, maxHeight: 200, overflowY: "auto" }}>
            {results.map((r) => (
              <button
                key={r.gid}
                type="button"
                onClick={() => {
                  onPatch({ gid: r.gid, stopName: r.name.split(",")[0] });
                  setQuery("");
                  setResults([]);
                }}
                style={{ display: "block", width: "100%", textAlign: "left", padding: "8px 10px", border: 0, background: "transparent", cursor: "pointer", fontSize: 13 }}
              >
                {r.name}
              </button>
            ))}
          </div>
        )}
      </div>
      <div className="de-rs-field" style={{ marginBottom: 8 }}>
        <label>{tr("Rows")}</label>
        <select
          value={o.rows || 5}
          onChange={(e) => onPatch({ rows: parseInt(e.target.value, 10) })}
          style={{ width: "100%", height: 32, borderRadius: 6 }}
        >
          {[3, 4, 5, 6, 7, 8, 10].map((n) => (
            <option key={n} value={n}>{n}</option>
          ))}
        </select>
      </div>
      {color(tr("Background"), "bg", "#0f172a")}
      {color(tr("Text color"), "fg", "#ffffff")}
      <p style={{ fontSize: 11, color: "#667085", marginTop: 8 }}>
        {tr("Times update on screens every minute. Line colours come from Västtrafik.")}
      </p>
    </div>
  );
}
